'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { format } from 'date-fns';
import { FiDownload } from 'react-icons/fi';

import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { exportVehicles, fetchVehicles, type Vehicle } from '../../api-custom/vehicles-api';
import { VehicleCard } from './vehicle-card';

type VehicleListProps = {
  showExport?: boolean;
};

const FUEL_TYPE_LABELS: Record<string, string> = {
  petrol: 'Benzín',
  diesel: 'Nafta',
  electric: 'Elektrina',
  hybrid: 'Hybrid',
  lpg: 'LPG',
};

const CSV_COLUMNS: { header: string; value: (vehicle: Vehicle) => string }[] = [
  { header: 'ŠPZ', value: v => v.vehicle_plate },
  { header: 'Značka a model', value: v => v.vehicle_type },
  { header: 'VIN', value: v => v.vin_number },
  { header: 'Palivo', value: v => FUEL_TYPE_LABELS[v.fuel_type] ?? v.fuel_type },
  { header: 'Objem motora (cm³)', value: v => v.engine_volume },
  { header: 'Výkon motora (kW)', value: v => v.engine_power },
  { header: 'Celková hmotnosť (kg)', value: v => v.total_weight },
  { header: 'Dátum evidencie', value: v => v.registration_date },
  { header: 'Poisťovacia spoločnosť', value: v => v.insurer_name || v.insurance_company_name },
  { header: 'Číslo poistky', value: v => v.policy_number },
  { header: 'Číslo zelenej karty', value: v => v.green_card_number },
  { header: 'Platnosť zelenej karty do', value: v => v.green_card_valid_until },
  { header: 'Poistené', value: v => (v.vehicle_insurance ? 'Áno' : 'Nie') },
  { header: 'Aktívne', value: v => (v.is_active ? 'Áno' : 'Nie') },
];

function toCsvCell(value: string) {
  const safe = value ?? '';
  return /[";\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function buildCsv(vehicles: Vehicle[]) {
  const rows = [
    CSV_COLUMNS.map(column => toCsvCell(column.header)).join(';'),
    ...vehicles.map(vehicle =>
      CSV_COLUMNS.map(column => toCsvCell(column.value(vehicle) ?? '')).join(';'),
    ),
  ];
  // BOM, aby Excel korektne načítal diakritiku
  return new Blob([`﻿${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function VehicleList({ showExport }: VehicleListProps) {
  const { t } = useTranslations();
  const authToken = useCustomerStore(s => s.me!.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const searchParams = useSearchParams();
  const page = Number(searchParams.get('page') || 1);

  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const query = { page, search: search.trim() || undefined };

  const { data, isLoading } = useQuery({
    queryKey: ['vehicles', locale, authToken, query],
    queryFn: () => fetchVehicles({ locale, authToken, query }),
  });

  /** Prejde všetky stránky zoznamu a vráti kompletný zoznam vozidiel. */
  async function fetchAllVehicles() {
    const all: Vehicle[] = [];
    let currentPage = 1;
    let lastPage = 1;

    do {
      const response = await fetchVehicles({
        locale,
        authToken,
        query: { page: currentPage, search: query.search },
      });
      all.push(...response.data);
      lastPage = response.meta?.last_page ?? currentPage;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  }

  async function handleExport() {
    setIsExporting(true);
    const today = format(new Date(), 'yyyy-MM-dd');

    try {
      // Preferujeme XLSX z backendu; ak endpoint nie je dostupný, vyskladáme CSV na klientovi.
      const blob = await exportVehicles({ locale, authToken, query: { search: query.search } });
      downloadBlob(blob, `moja-flotila-${today}.xlsx`);
    } catch {
      try {
        const vehicles = await fetchAllVehicles();

        if (!vehicles.length) {
          toast({ title: 'Nie sú k dispozícii žiadne vozidlá na export.', variant: 'destructive' });
          return;
        }

        downloadBlob(buildCsv(vehicles), `moja-flotila-${today}.csv`);
      } catch {
        toast({ title: t('text4063'), variant: 'destructive' });
      }
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-border bg-white p-6 sm:flex-row sm:items-center sm:p-8">
        <Input
          type="text"
          variant="outlined"
          className={{ wrapper: 'rounded-[6px]' }}
          placeholder="Hľadať podľa ŠPZ, VIN alebo značky"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {showExport && (
          <Button
            type="button"
            variant="outline"
            className="w-full shrink-0 rounded-[6px] sm:w-fit"
            onClick={handleExport}
            isLoading={isExporting}
          >
            <FiDownload size={18} /> Exportovať zoznam
          </Button>
        )}
      </div>

      {isLoading ? (
        <GenericLoader />
      ) : (
        <div className="flex w-full flex-col gap-3">
          {!data?.data.length && (
            <p className="w-full rounded-2xl border border-border bg-white py-12 text-center text-sm text-muted-foreground">
              Nenašli sa žiadne vozidlá.
            </p>
          )}

          {data?.data.map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}

          {!!data?.meta?.links && (
            <div className="flex w-full items-center justify-center pt-4">
              <Pagination links={data.meta.links} resetWhen={[search]} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
